import { useState } from "react";
import { MapPin, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const PETROLE = "#284E55";

const computePrice = (n) => {
  if (n <= 0) return 0;
  let p = 249;
  if (n >= 2) p += 179;
  if (n >= 3) p += 99 * (n - 2);
  return p;
};

const TerritoryCard = ({ t, selected, onToggle, disabled, main }) => (
  <label
    data-testid={`territory-card-${t.code}`}
    className={`flex items-start gap-3 rounded-2xl border-2 p-5 transition-colors duration-200 ${
      selected ? "border-[#284E55] bg-[#284E55]/5" : "border-[#EDEAE3] bg-white"
    } ${disabled ? "" : "cursor-pointer hover:border-[#284E55]/40"}`}
  >
    {!main && (
      <input
        type="checkbox"
        data-testid={`territory-checkbox-${t.code}`}
        checked={selected}
        disabled={disabled}
        onChange={onToggle}
        className="mt-1 h-5 w-5 shrink-0 accent-[#284E55]"
      />
    )}
    <div>
      <p className="flex items-center gap-2 text-sm font-bold text-navy">
        <MapPin className="h-4 w-4 shrink-0" style={{ color: PETROLE }} /> {t.name}
        {main && <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: PETROLE }}>(votre territoire)</span>}
      </p>
      <p className="mt-1 text-xs text-stone-500">{t.department} · {t.region} · {t.commune_count} communes</p>
    </div>
  </label>
);

export const PartnerTerritorySection = ({ token, territory, onUpdated }) => {
  const [selected, setSelected] = useState(() => (territory.proposed_territory ? [territory.proposed_territory.id] : []));
  const [engaged, setEngaged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const choose = async () => {
    setSubmitting(true);
    try {
      await api.post(`/partner-portal/${token}/territory/choose`, { territory_ids: selected });
      toast.success("Territoire choisi");
      onUpdated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (territory.activation_status === "VALIDE") {
    if (!territory.proposed_territory) {
      return (
        <div data-testid="territory-section" className="mt-8 rounded-2xl border-2 border-[#EDEAE3] bg-white p-6">
          <p className="text-sm text-stone-500">
            Aucun territoire n'a encore été identifié pour votre secteur. CozyHome vous contactera directement.
          </p>
        </div>
      );
    }
    return (
      <div data-testid="territory-section" className="mt-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Votre territoire CozyHome</p>
        <div className="mt-3 space-y-3">
          <TerritoryCard t={territory.proposed_territory} selected main disabled />
          {territory.complementary_territories.length > 0 && (
            <>
              <p className="pt-2 text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Territoires complémentaires disponibles</p>
              {territory.complementary_territories.map((t) => (
                <TerritoryCard key={t.id} t={t} selected={selected.includes(t.id)} onToggle={() => toggle(t.id)} />
              ))}
            </>
          )}
        </div>
        <div data-testid="territory-price-summary" className="mt-5 rounded-2xl p-5 text-white" style={{ backgroundColor: PETROLE }}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">Total mensuel HT</p>
          <p className="mt-1 font-display text-3xl font-medium">
            {computePrice(selected.length)} € <span className="text-base text-white/70">HT / mois</span>
          </p>
          <p className="mt-2 text-xs text-white/70">249 € pour le 1er secteur, 179 € pour le 2e, 99 € à partir du 3e.</p>
        </div>
        <label data-testid="territory-engagement-checkbox" className="mt-4 flex cursor-pointer items-start gap-3 rounded-2xl border-2 border-[#EDEAE3] bg-white px-5 py-4">
          <input type="checkbox" checked={engaged} onChange={(e) => setEngaged(e.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 accent-[#284E55]" />
          <span className="text-xs leading-relaxed text-navy/80">
            J'accepte les conditions de l'abonnement Partenaire CozyHome (facturation mensuelle, résiliable, secteur
            attribué après vérification par CozyHome).
          </span>
        </label>
        <button
          data-testid="territory-choose-button"
          onClick={choose}
          disabled={submitting || selected.length === 0 || !engaged}
          className="mt-5 flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-white transition-opacity duration-300 hover:opacity-90 disabled:opacity-60"
          style={{ backgroundColor: PETROLE }}
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          Choisir ce territoire
        </button>
      </div>
    );
  }

  if (["TERRITOIRE_CHOISI", "PAIEMENT_EN_ATTENTE", "DOCUMENTS_EN_ATTENTE", "ESPACE_ACTIF_TERRITOIRE_EN_ATTENTE"].includes(territory.activation_status)) {
    return (
      <div data-testid="territory-section" className="mt-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Vos territoires choisis</p>
        <div className="mt-3 space-y-3">
          {territory.chosen_territories.map((t) => (
            <TerritoryCard key={t.id} t={t} selected main disabled />
          ))}
        </div>
        <div data-testid="territory-status-banner" className="mt-5 rounded-2xl p-5" style={{ backgroundColor: `${PETROLE}0D`, color: PETROLE }}>
          <p className="text-sm font-bold">{territory.monthly_price_ht} € HT / mois</p>
          <p className="mt-2 text-sm leading-relaxed">
            {territory.activation_status === "DOCUMENTS_EN_ATTENTE"
              ? "Certains documents doivent encore être validés avant l'activation de votre territoire."
              : territory.activation_status === "ESPACE_ACTIF_TERRITOIRE_EN_ATTENTE"
              ? "Votre Espace Partenaire est actif. Votre territoire reste en attente d'activation par CozyHome : aucun projet ne vous sera transmis avant cette activation."
              : "Votre espace professionnel sera entièrement activé après validation et activation de votre territoire CozyHome."}
          </p>
        </div>
      </div>
    );
  }

  return null;
};
